import { Badge, Box, Button, Drawer, IconButton, InputAdornment, TextField, Theme, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { DialogCloseIcon, FilterIcon, PlusIcon, SearchIcon } from '../../assets/SvgIcons'
import CustomTable from '../investorOverview/common/customTable';
import CustomFilter from '../investorOverview/common/CustomFilter';
import { PaginationObj, SortingObj } from '../investorOverview/types';
import { useDebounce } from '../../utils/useDebounce';
import ViewBankTableBody, { ViewBankTableBodyResponsive, viewBankTableHeader } from './ViewBankTableBody';
import BreadCrum from '../../lib/BreadCrum';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { BankListObj, BanksListRes, fetchBanksList } from '../../redux-store/actions/bank';
import { whiteSpaceOnlyRegex } from '../../utils/regex';
import { RootStateType } from '../../redux-store/reducers';
import { USER_ROLES } from '../../utils/constant';

export default function ViewBank(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const initialRouter = history.location.pathname?.split('/').filter((item) => item !== '')
    const [banksListLoading, setBanksListLoading] = useState<boolean>(false);
    const [banksList, setBanksList] = useState<Array<BankListObj>>([]);
    const { selectedPortfolio } = useSelector((state: RootStateType) => state.selectedPortfiolio);



    const [searchText, setSearchText] = useState<string>('');
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: ""
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })
    const [totalFilters, setTotalFilters] = useState<number>(0);
    const theme = useTheme();
    const debounceSearchText = useDebounce(searchText, 500);
    const { role = "" } = useSelector((state: RootStateType) => state.auth);
    const { applicationId = "" } = useSelector((state: RootStateType) => state.id);
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
    const [banksListCount, setBanksListCount] = useState<number>(0);


    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false);
    };

    const isFiltersLoading = false;
    const handleFiltersClearAll = () => { console.log('filters clear') };
    const handleApplyFilters = () => { console.log('filters apply') };

    const fetchListOfBanks = async ({
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
    }: {
        searchInput?: string;
        currentPage?: number;
        rowsPerPage?: number;
    }) => {
        try {
            setBanksListLoading(true);
            const listOfBanks = await (dispatch(fetchBanksList({
                ...(role === USER_ROLES.INVESTOR && { applicationId }),
                searchInput,
                currentPage,
                rowsPerPage
            })) as unknown as Promise<BanksListRes>);
            setBanksList(listOfBanks?.result ?? []);
            setBanksListCount(listOfBanks?.count ?? 0);

        } catch (e) {
            console.log('ERROR!', e)
        } finally {
            setBanksListLoading(false);
        }
    }

    useEffect(() => {
        fetchListOfBanks({
            searchInput: debounceSearchText.trim(),
        })
    }, [debounceSearchText])

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchListOfBanks({
            searchInput: debounceSearchText.trim(),
            rowsPerPage: Number(event.target.value),
        });
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchListOfBanks({
            searchInput: debounceSearchText.trim(),
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
    }

    const rmRouterPath =
        [
            {
                name: 'Dashboard',
                path: '/dashboard',
                warning: false
            },
            {
                name: 'Update Bank',
                path: `/${initialRouter[0]}`,
            },
        ]

    const investorRouterPath = [
        {
            name: 'Dashboard' + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
        },
        {
            name: 'Update Bank',
            path: `/${initialRouter[0]}`,
        },

    ]
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BreadCrum array={role === USER_ROLES.RM ? rmRouterPath : investorRouterPath} />
            </Box>
            <Box mb="25px" mt={2}>
                <Typography variant='h1' color="text.primary">
                    View Bank Status
                </Typography>
            </Box>
            <Box
                sx={(theme: Theme) => ({
                    marginTop: "15.5px",
                    borderRadius: "25px",
                    padding:{xs:"10px 15px",sm:"20px 30px"},
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                })}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Search Input */}
                    <TextField
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                        onMouseDown={handleClearSearch}
                                    >
                                        <DialogCloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: (theme: Theme) => ({
                                borderRadius: '10px',
                                height: '47.17px',
                                border: `1px solid ${theme.palette.text.secondary}B2`,
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    paddingLeft: '10px',
                                    ...theme.typography.body_500,
                                    color: theme.palette.text.primary,
                                    '::placeholder': {
                                        ...theme.typography.subtitle_400,
                                        color: theme.palette.text.secondary,
                                        opacity: 1,
                                    },
                                },
                            }),
                        }}
                        placeholder="Search"
                        value={searchText}
                        onChange={handleSearchTextChange}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '40px', mt: { xs: '20px', sm: '0px' } }}>
                        {/* Filters */}
                        {/* <Button
                            variant="text"
                            startIcon={
                                <Badge
                                    showZero
                                    // badgeContent={totalFilters}
                                    badgeContent={0}
                                    sx={(theme: Theme) => ({
                                        '& .MuiBadge-badge': {
                                            right: 0,
                                            top: -3,
                                            backgroundColor: theme.palette.primary[500],
                                            color: theme.palette.common.white,
                                            ...theme.typography.subtitle_600
                                        },
                                    })}
                                >
                                    <FilterIcon />
                                </Badge>
                            }
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                padding: "5px",
                                ...theme.typography.body_500,
                                "&:hover": {
                                    backgroundColor: 'unset'
                                }
                            })}
                            TouchRippleProps={{
                                style: {
                                    color: theme.palette.grey[300]
                                }
                            }}
                            onClick={() => setFilterDrawerOpen(true)}
                        >
                            Filters
                        </Button> */}

                        {/* Add Bank */}
                        <Button sx={(theme: Theme) => ({
                            background: theme.palette.primary[500],
                            borderRadius: '4px',
                            width: '100%',
                            padding: '7px',
                            gap: '5px',
                            '&:hover': {
                                backgroundColor: theme.palette.primary[500]
                            }
                        })}
                            onClick={() => history.push('/bank/updateBank')}>
                            <PlusIcon />
                            <Typography sx={(theme: Theme) => ({
                                color: theme.palette.textDark.primary,
                                fontWeight: 500
                            })}>Add Bank</Typography>
                        </Button>
                    </Box>
                </Box>

                {/* View Bank Table */}
                <Box sx={{ mt: '35px' }}>
                    <CustomTable
                        isLoading={banksListLoading}
                        tableBody={
                            <ViewBankTableBody data={banksList ?? []} />
                        }
                        tableData={banksList ?? []}
                        tableHeaderData={viewBankTableHeader}
                        isPaginationVisible={true}
                        tableDataCount={banksListCount}
                        // handleSort={handleSort}
                        // order={tableSortingObj.order}
                        // orderBy={tableSortingObj.orderBy}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        handlePageChange={handlePageChange}
                        rowsPerPage={tablePaginationObj.rowsPerPage}
                        currentPage={tablePaginationObj.currentPage}
                        rowsPerPageOptions={[5, 10, 20, 30]}
                        tableBodyLoaderRowCustomStyles={{
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        }}
                        tableHeadRowCustomStyles={{
                            '& th': {
                                backgroundColor: '#EDF2F759',
                            },
                            '& th:first-of-type': {
                                paddingLeft: "0px",
                            }
                        }}
                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        manualResponsiveBody={true}
                        hideResponsiveHeader={true}
                        ManualResponsiveBodyComponent={
                            <ViewBankTableBodyResponsive
                            data={banksList ?? []}
                            tableHeaderData={viewBankTableHeader}

                            


                            />
                        }
                        loadingRowsNum={5}
                    />
                </Box>
            </Box>
            <Drawer
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        padding: "25px",
                        width: "450px"
                    }
                }}
                ModalProps={{
                    sx: (theme: Theme) => ({
                        backgroundColor: `${theme.palette.common.black}03`
                    })
                }}
            >
                <CustomFilter
                    isLoading={isFiltersLoading}
                    handleClearAll={handleFiltersClearAll}
                    handleApplyFilters={handleApplyFilters}
                    showCloseIcon={true}
                    showAmcFilter={false}
                    showCategoryFilter={false}
                    showRiskFilter={false}
                    handleCloseIconClick={handleCloseFilterDrawer}
                    defaultExpandedFilter="productType"
                />
            </Drawer>
        </>
    )
}

